import {
  DialogContent,
  Dialog,
  DialogProps,
  useEventCallback,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { styled } from '@mui/styles';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import AccordionFilter from './filter';
import { __PERSON_NEW_API_URL__, __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { CadastralData, ResponseAnprData } from 'shell/models/person';
import { useFormik } from 'formik';
import {
  FilterFamilyVM,
  TableFamiliarBodyVM,
  TableFamiliarVM,
  TableNewFamilySubjectsVM,
} from 'practice/view-models/practice-edit-residence';
import { DateTime } from 'luxon';
import TableFamilyMembers from './table-family-members';
import { Address } from 'shell/models/person/residence';
import AccordionNewFamilySubjects from './accordion-new-family-subjects';
import { toast } from 'react-toastify';
import { FormActualResidenceVM } from 'practice/view-models/pratice-actual-residence';
import { transformActualResidencePractice } from 'practice/utils/transform-residence-vm';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';

export const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '1100px',
      },
}));

interface ModalFamilyProps {
  open: boolean;
  onClose: (open: boolean) => void;
  onConfirm: (
    table?: TableNewFamilySubjectsVM,
    residence?: FormActualResidenceVM,
    tableFamiliar?: TableFamiliarVM,
    item?: TableFamiliarBodyVM
  ) => void;
  initialValuesTableNewFamily?: TableNewFamilySubjectsVM;
  initialValuesResidence?: FormActualResidenceVM;
  praticaKey?: {
    cdEnte: string;
    tipoPratica: string;
    idAnnoPratica: number;
    pgPratica: number;
  } | null;
}

type FamilyResponse = { body: Array<TableFamiliarBodyVM>; problems: SaveDataAnpr };

export type FamilyModalVM = {
  filter?: FilterFamilyVM;
  tableFamiliar?: TableFamiliarVM;
  tableNewFamilySubjects?: TableNewFamilySubjectsVM;
};

const ModalFamilyComponent = ({
  open,
  onClose,
  onConfirm,
  initialValuesTableNewFamily,
  initialValuesResidence,
  praticaKey,
}: ModalFamilyProps): React.ReactElement => {
  const [openAccordionNewFamily, setOpenAccordionNewFamily] = useState<boolean>(false);

  const [withSearch, setWithSearch] = useState<boolean>(false);

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const saveData = useHttpClient(
    `${__PRACTICE_API_URL__}/visualizzazione-scelta-famiglia/inserisci`,
    {
      options: {
        method: 'POST',
      },
    }
  );

  const onSave = useEventCallback(() => {
    if (!withSearch) {
      // se si chiude senza ricerca
      onClose(true);

      return;
    }

    const tableNewFamilySubjectsBody =
      formContext.values.tableNewFamilySubjects?.body?.slice() || [];

    const family = (
      tableNewFamilySubjectsBody?.filter((element) => !element.hideRelationship) || []
    ).map((item) => ({
      ...item.subject,
      legameSoggetto: {
        ...item.subject?.legameSoggetto,
        codiceLegame: item.relationshipValue || item.relationship,
      },
      selected: true,
    }));

    const personData = {
      visualizzazioneSceltaFamigliaList: family,
      tipoRegistro: 'IAIRE',
      idAnnoPratica: praticaKey?.idAnnoPratica,
      progressivoPratica: praticaKey?.pgPratica,
    };

    (saveData({ data: personData }) as ResponsePromise<void>).then(() => {
      const [item] = formContext.values.tableNewFamilySubjects?.body || [];

      //per recuperrare i dati catastali dall oggetto residence
      const residenceObj: FormActualResidenceVM | undefined =
        formContext.values.tableNewFamilySubjects?.residence || undefined;

      if (!item) {
        return;
      }

      toast.success('Operazione eseguita con successo');

      const datCatastali: CadastralData = {
        foglio: residenceObj?.italian?.sheet || '',
        particella: residenceObj?.italian?.particle || '',
        subalterno: residenceObj?.italian?.sub || '',
        categoriaCatastale: residenceObj?.italian?.cat || '',
        sezione: residenceObj?.italian?.section || '',
      };
      const residence: FormActualResidenceVM = transformActualResidencePractice(
        item.residenza,
        datCatastali,
        residenceObj?.italian?.isHomeless
      );

      onConfirm(
        formContext.values.tableNewFamilySubjects,
        residence,
        formContext.values.tableFamiliar
      );
    });
  });

  const formContext = useFormik<FamilyModalVM>({
    initialValues: {
      filter: undefined,
      tableFamiliar: undefined,
      tableNewFamilySubjects: {
        ...(initialValuesTableNewFamily || {}),
        residence: initialValuesResidence || undefined,
      },
    },
    onSubmit: onSave,
    enableReinitialize: true,
  });

  const [person, getPerson] = useHttpFetch<HttpResponse<ResponseAnprData>, FamilyResponse>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            // Fixed value for request family
            tipoScheda: '1',
            codiceFiscale: formContext.values.filter?.fiscalCode || undefined,
            cognome: formContext.values.filter?.surname || undefined,
            nome: formContext.values.filter?.name || undefined,
            senzaCognome:
              formContext.values.filter?.withoutSurname || !formContext.values.filter?.surname
                ? '1'
                : undefined,
            senzaNome:
              formContext.values.filter?.withoutName || !formContext.values.filter?.name
                ? '1'
                : undefined,
          },
          datiRichiesta: {
            schedaAnagraficaRichiesta: '1',
            ricercaNazionale: '1',
            // Fixed value for request family
            datiAnagraficiRichiesti: ['02', '10', '21', '22'],
          },
        },
      },
      transformer: (response) => {
        const body = response.data.payload?.risposta3002OK?.listaSoggetti?.datiSoggetto?.map(
          (subject) => {
            let residence = '';

            if ((subject.residenza || [])[0]?.indirizzo?.comune?.nomeComune) {
              // valorizzare con comune
              const indirizzo: Address = (subject.residenza || [])[0]?.indirizzo || {};

              residence = `${indirizzo.comune?.nomeComune || ''} ${
                indirizzo.comune?.siglaProvinciaIstat
                  ? `(${indirizzo.comune.siglaProvinciaIstat})`
                  : ''
              }`;
            } else if ((subject.residenza || [])[0]?.localitaEstera) {
              const descrizioneStato =
                (subject.residenza || [])[0]?.localitaEstera?.indirizzoEstero?.localita
                  ?.descrizioneStato || '';
              const descrizioneConsolato =
                (subject.residenza || [])[0]?.localitaEstera?.consolato?.descrizioneConsolato || '';
              residence = [descrizioneStato, descrizioneConsolato].filter(Boolean).join(' ');
            } else {
              const indirizzo: Address = (subject.residenza || [])[0]?.indirizzo || {};
              // valorizzare con toponimo
              residence = `${indirizzo?.toponimo?.denominazioneToponimo || ''},${
                indirizzo?.numeroCivico?.numero
              }${
                indirizzo.numeroCivico?.esponente1 || indirizzo.numeroCivico?.lettera
                  ? `/${indirizzo.numeroCivico?.esponente1 || ''}${
                      indirizzo.numeroCivico?.lettera || ''
                    }`
                  : ''
              }`;
            }

            let birthplace = '';
            // birthplace check for object
            if (subject.generalita?.luogoNascita?.localita) {
              birthplace = `${subject.generalita?.luogoNascita?.localita.descrizioneLocalita} ${
                subject.generalita?.luogoNascita?.localita.descrizioneStato
                  ? `(${subject.generalita?.luogoNascita?.localita.descrizioneStato})`
                  : ''
              }`;
            }

            if (subject.generalita?.luogoNascita?.comune) {
              birthplace = `${subject.generalita?.luogoNascita?.comune.nomeComune} ${
                subject.generalita?.luogoNascita?.comune.siglaProvinciaIstat
                  ? `(${subject.generalita?.luogoNascita?.comune.siglaProvinciaIstat})`
                  : ''
              }`;
            }

            return {
              surname: subject.generalita?.cognome || '',
              name: subject.generalita?.nome || '',
              fiscalCode: subject.generalita?.codiceFiscale?.codFiscale || '',
              birthDate: subject.generalita?.dataNascita
                ? DateTime.fromISO(subject.generalita?.dataNascita).toFormat('dd/MM/yyyy')
                : '',
              relationship: subject.legameSoggetto?.codiceLegame || '',
              idAnpr: subject.famigliaConvivenza?.idFamigliaConvivenzaANPR || '',
              IdCardMunicipality:
                subject.famigliaConvivenza?.idFamigliaConvivenzaComune?.idFamigliaConvivenza || '',
              birthplace,
              gender: subject.generalita?.sesso || '',
              residenza: (subject.residenza || [])[0],
              residence,
              state: subject.generalita?.soggettoAIRE === 'S' ? 'Iscritto AIRE' : '',
              isHomeless: subject.attributiSoggetto?.senzaFissaDimora === 'S',
            } as TableFamiliarBodyVM;
          }
        );

        if ((response.data.payload?.rispostaKO?.listaErrori?.errore || [])?.length > 0) {
          // mostra errors
          setVisibility({
            error: true,
            warning: false,
          });
        }

        if ((response.data.payload?.risposta3002OK?.listaAnomalie?.errore || [])?.length > 0) {
          // mostra warnings
          setVisibility({
            error: false,
            warning: true,
          });
        }

        return {
          body,
          problems: {
            warnings: (response.data.payload?.risposta3002OK?.listaAnomalie?.errore || []).map(
              (warning) => ({
                id: warning.codiceErroreAnomalia,
                value: warning.testoErroreAnomalia,
              })
            ),
            errors: (response.data.payload?.rispostaKO?.listaErrori?.errore || []).map((error) => ({
              id: error.codiceErroreAnomalia,
              value: error.testoErroreAnomalia,
            })),
          },
        } as FamilyResponse;
      },
    }
  );

  const onSearch = useEventCallback(() => {
    // ricerca familiari
    getPerson({
      data: {
        criteriRicerca: {
          // Fixed value for request family
          tipoScheda: '1',
          codiceFiscale: formContext.values.filter?.fiscalCode || undefined,
          cognome: formContext.values.filter?.surname || undefined,
          nome: formContext.values.filter?.name || undefined,
          senzaCognome:
            formContext.values.filter?.withoutSurname || !formContext.values.filter?.surname
              ? '1'
              : undefined,
          senzaNome:
            formContext.values.filter?.withoutName || !formContext.values.filter?.name
              ? '1'
              : undefined,
        },
        datiRichiesta: {
          schedaAnagraficaRichiesta: '1',
          ricercaNazionale: '0',
          // Fixed value for request family
          datiAnagraficiRichiesti: ['02', '10', '21', '22'],
        },
      },
    });
  });

  const isDisabled = useMemo(() => {
    if (!formContext.values.tableFamiliar?.body?.find((item) => item.isActive)) {
      return true;
    }

    const personWithoutRelationship = formContext.values.tableNewFamilySubjects?.body?.find(
      (item) => !item.relationshipValue
    );

    if (personWithoutRelationship) {
      // se non è ancora stata inserita la parentela
      return true;
    }

    return false;
  }, [formContext.values.tableFamiliar?.body, formContext.values.tableNewFamilySubjects?.body]);

  const castedAnprData = (person.data || {}) as FamilyResponse;

  useEffect(() => {
    if ((castedAnprData?.body || []).length > 0) {
      // true se è ststa effettuata la ricerca
      setWithSearch(true);
    }
  }, [castedAnprData]);

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.problems?.errors && castedAnprData?.problems?.errors?.length) {
      errorToVisualize.title =
        castedAnprData?.problems?.errors &&
        castedAnprData?.problems?.errors?.length &&
        castedAnprData?.problems?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.problems?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedAnprData?.problems?.errors;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData]);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <PmiDialog
      fullScreen={fullScreen}
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
        Ricerca persona
      </PmiDialogTitle>
      <DialogContent style={{ padding: '30px 24px' }}>
        <SearchMessages
          type={MessageType.Error}
          title={errorMessage.title || ''}
          messages={errorMessage.messages || []}
          visibility={errorMessage.visibility || false}
          onChangeVisibility={onVisibility}
        />
        <SearchMessages
          type={MessageType.Warning}
          title={
            castedAnprData?.problems?.warnings && castedAnprData?.problems?.warnings?.length > 1
              ? `Ci sono ${castedAnprData?.problems?.warnings?.length} warning`
              : `C'è 1 warning`
          }
          messages={castedAnprData?.problems?.warnings || []}
          visibility={
            visibility.warning !== undefined
              ? visibility.warning
              : !!(
                  castedAnprData?.problems &&
                  castedAnprData?.problems?.warnings &&
                  castedAnprData?.problems?.warnings?.length > 0
                )
          }
          onChangeVisibility={onVisibility}
        />
        <AccordionFilter onSearch={onSearch} formContext={formContext} />
        <TableFamilyMembers
          familyMembers={(castedAnprData?.body || []) as Array<TableFamiliarBodyVM>}
          onClose={onClose}
          formContext={formContext}
          setOpenAccordionNewFamily={(value: boolean) => setOpenAccordionNewFamily(value)}
        />
        <AccordionNewFamilySubjects
          formContext={formContext}
          open={openAccordionNewFamily}
          setOpen={(value: boolean) => setOpenAccordionNewFamily(value)}
          praticaKey={praticaKey}
        />
      </DialogContent>
      <DialogActions>
        <PmiButton
          variant="contained"
          color="primary"
          disabled={isDisabled}
          onClick={() => formContext.handleSubmit()}
        >
          Conferma
        </PmiButton>
        <PmiButton variant="outlined" color="primary" type="button" onClick={() => onClose(true)}>
          Chiudi
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

ModalFamilyComponent.displayName = 'Modal family';

const ModalFamily = memo(ModalFamilyComponent);

export default ModalFamily;
